import BigNumber from "bignumber.js";

import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { BN } from "@utils/BN";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@components/Icon";
import { theme } from "@themes/themes";

import { useMedia } from "@hooks/useMedia";
import { useStores } from "@stores";

import { Button, Dialog, Slider, Text } from "@src/ui-kit";
import { Sheet } from "@src/ui-kit/Sheet";

interface SelectLeverage {
  leverage: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (leverage: number) => void;
}

const DEFAULT_MIN_LEVERAGE = 1;
const DEFAULT_MAX_LEVERAGE = 50;
const DEFAULT_LEVERAGE_STEP = 1;

export const SelectLeverage: React.FC<SelectLeverage> = observer(({ leverage, isOpen, onOpenChange, onChange }) => {
  const { t } = useTranslation();
  const { marketStore, accountStore } = useStores();
  const [currentLeverage, setCurrentLeverage] = useState(leverage);
  const { mobile } = useMedia();

  useEffect(() => {
    setCurrentLeverage(leverage);
  }, [leverage]);

  const activeMarket = marketStore.activeMarket;
  const activeMarketBaseToken = marketStore.baseToken;
  const activeMarketQuoteToken = marketStore.quoteToken;
  const marketParams = marketStore.getMarketConfig(activeMarket?.addr || "");
  const leverageFilter = activeMarket ? marketStore.getLeverageFilter(activeMarket.symbol) : null;

  const minLeverage = leverageFilter?.minLeverage ?? DEFAULT_MIN_LEVERAGE;
  const maxLeverage =
    leverageFilter?.maxLeverage ?? new BN(activeMarket?.max_leverage ?? DEFAULT_MAX_LEVERAGE).toNumber();
  const leverageStep = leverageFilter?.leverageStep ?? DEFAULT_LEVERAGE_STEP;

  const stepDecimals = leverageStep < 1 ? (String(leverageStep).split(".")[1]?.length ?? 0) : 0;

  const roundToStep = (value: number) => {
    const rounded = Math.round(value / leverageStep) * leverageStep;
    const clamped = Math.max(minLeverage, Math.min(maxLeverage, rounded));
    return Number(clamped.toFixed(stepDecimals));
  };

  const formatLeverage = (value: number) => value.toFixed(stepDecimals);

  const handleSliderChange = (value: number | number[]) => {
    const raw = Array.isArray(value) ? value[0] : value;
    setCurrentLeverage(roundToStep(raw));
  };

  const sliderMarks = useMemo(() => {
    const marks = [] as { value: number; label: string }[];
    const totalMarks = 20;
    const step = (maxLeverage - minLeverage) / (totalMarks - 1);

    for (let i = 0; i < totalMarks; i++) {
      const value = roundToStep(minLeverage + step * i);
      marks.push({ value, label: `${formatLeverage(value)}x` });
    }

    return marks;
  }, [maxLeverage, minLeverage, leverageStep, stepDecimals]);

  const handleConfirm = () => {
    onChange(currentLeverage);
    onOpenChange(false);
  };

  const decrease = () => setCurrentLeverage((v) => roundToStep(v - leverageStep));
  const increase = () => setCurrentLeverage((v) => roundToStep(v + leverageStep));

  useEffect(() => {
    if (isOpen) {
      setCurrentLeverage(leverage);
    }
  }, [isOpen]);

  const header = (
    <Dialog.Header>
      <Dialog.Title>
        {activeMarket
          ? t("leverage.adjustTitle", { symbol: activeMarket.base_symbol, maxLeverage })
          : t("leverage.adjustTitleNoSymbol", { maxLeverage })}
      </Dialog.Title>
      <Dialog.Close aria-label={t("common.close", "Close")} data-testid="leverage:close" asChild>
        <CloseButton type="button">
          <Icon data-testid="leverage:close-icon" icon="ri-close-large-line" />
        </CloseButton>
      </Dialog.Close>
    </Dialog.Header>
  );

  const content = (
    <DialogBody>
      <MarginModeRow justify="between">
        <Flex align="center" gap="2">
          <Text color="gray-11" size="2">
            {t("leverage.marginMode")}
          </Text>
          <MarginBadge>
            <Text font="body12" size="2">
              {t("leverage.cross")}
            </Text>
          </MarginBadge>
        </Flex>
      </MarginModeRow>
      <LeverageControlSection>
        <Flex align="center" gap="0" justify="between" width="100%">
          <RoundButton
            aria-label={t("leverage.decrease", "Decrease leverage")}
            data-testid="leverage:minus"
            variant="classic"
            onClick={decrease}
          >
            <Icon icon="ri-subtract-line" />
          </RoundButton>
          <ValueWrapper>
            <ValueNumber data-testid="leverage:value">{formatLeverage(currentLeverage)}</ValueNumber>
            <ValueX>x</ValueX>
          </ValueWrapper>
          <RoundButton
            aria-label={t("leverage.increase", "Increase leverage")}
            data-testid="leverage:plus"
            variant="classic"
            onClick={increase}
          >
            <Icon icon="ri-add-line" />
          </RoundButton>
        </Flex>
        <SliderWrapper>
          <Slider
            data-testid="leverage:slider"
            marks={sliderMarks}
            max={maxLeverage}
            min={minLeverage}
            step={leverageStep}
            value={currentLeverage}
            valueFormatter={(v) => `${formatLeverage(v)}x`}
            hideLabels
            onChange={handleSliderChange}
          />
        </SliderWrapper>
      </LeverageControlSection>
      <InfoCard direction="column" gap="8px">
        <Bullet>{t("leverage.changeWarning", { symbol: activeMarketBaseToken?.symbol || "APT" })}</Bullet>
      </InfoCard>
      <FooterSection>
        <FooterNote>
          <Flex direction="column" gap="4px">
            <Text color="gray-11" font="body12">
              {t("leverage.maxPositionValue")}
            </Text>
            <Text data-testid="leverage:max-position-value" font="body14">
              {(() => {
                try {
                  const portfolio = accountStore.portfolio;
                  const price = marketStore.marketPrice;
                  if (!portfolio || !price || !activeMarketQuoteToken || !activeMarketBaseToken) return "-";
                  const availableQuote = new BN(BN.formatUnits(portfolio.available_balance, 0));
                  if (availableQuote.lte(BN.ZERO)) return `0 ${activeMarketBaseToken.symbol}`;
                  const baseAmount = availableQuote.multipliedBy(currentLeverage).dividedBy(price);
                  return `${baseAmount.toDecimalPlaces(marketParams.pricePrecision, BigNumber.ROUND_UP).toFormat()} ${activeMarketBaseToken.symbol}`;
                } catch {
                  return "-";
                }
              })()}
            </Text>
          </Flex>
        </FooterNote>
        <ButtonStyled data-testid="leverage:confirm" size="3" fullwidth onClick={handleConfirm}>
          {t("leverage.confirmLeverage", { leverage: formatLeverage(currentLeverage) })}
        </ButtonStyled>
      </FooterSection>
    </DialogBody>
  );

  if (mobile) {
    return (
      <Sheet data-testid="leverage:sheet" open={isOpen} showDragHandle onOpenChange={onOpenChange}>
        <SheetHeader>
          <Dialog.Title>
            {activeMarket
              ? t("leverage.adjustTitle", { symbol: activeMarket.base_symbol, maxLeverage })
              : t("leverage.adjustTitleNoSymbol", { maxLeverage })}
          </Dialog.Title>
        </SheetHeader>
        {content}
      </Sheet>
    );
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={() => {
        onOpenChange(!isOpen);
      }}
    >
      <Dialog.Overlay />
      <Dialog.Content data-testid="leverage:dialog">
        {header}
        {content}
      </Dialog.Content>
    </Dialog.Root>
  );
});

const DialogBody = styled.div`
  padding: 24px;
`;

const SheetHeader = styled.div`
  padding: 16px 24px 0;
`;

const MarginModeRow = styled(Flex)`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.color("gray-5")};
`;

const MarginBadge = styled.div`
  padding: 2px 8px;
  border-radius: 4px;
  background: ${theme.color("gray-4")};
  border: 1px solid ${theme.color("gray-5")};
`;

const LeverageControlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 16px;
`;

const SliderWrapper = styled.div`
  margin-top: 8px;
`;

const ButtonStyled = styled(Button)`
  border-radius: 8px;
`;

const InfoCard = styled(Flex)`
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${theme.color("gray-5")};
  background: ${theme.color("gray-2")};
`;

const Bullet = styled(Text)`
  ${theme.font.body12};
` as unknown as typeof Text;

const RoundButton = styled(Button)`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${theme.color("gray-4")} !important;
  border: 1px solid ${theme.color("gray-5")} !important;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FooterNote = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${theme.color("gray-3")};
  border: 1px solid ${theme.color("gray-5")};
`;

const ValueWrapper = styled(Flex)`
  align-items: baseline;
  gap: 6px;
  justify-content: center;
  flex: 1;
`;

const ValueNumber = styled.span`
  ${theme.font.body18};
  font-size: 28px;
  line-height: 1;
  color: ${theme.color("accent-contrast")};
`;

const ValueX = styled.span`
  ${theme.font.body14};
  color: ${theme.color("gray-11")};
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
